/**
 * Centralized UI Constants
 * All spacing, sizing, and layout constants in one place for consistency
 * COMPACT MODERN UI - Minimal spacing, small components
 */

// Spacing values (compact - reduced from standard 8px base)
export const SPACING = {
  xs: 0.25, // 2px
  sm: 0.5, // 4px
  md: 1, // 8px
  lg: 1.5, // 12px
  xl: 2, // 16px
  xxl: 3, // 24px
  xxxl: 4, // 32px
};

// Icon sizes (smaller defaults)
export const ICON_SIZES = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  xxl: 24,
  xxxl: 32,
  huge: 48,
};

// Layout dimensions (compact)
export const LAYOUT = {
  sidebarWidth: 220,
  sidebarWidthCollapsed: 56,
  topBarHeight: 48,
  maxContentWidth: 1400,
  cardMaxWidth: 360,
  dialogMaxWidth: 540,
  drawerWidth: 280,
};

// Border radius values (slightly reduced for modern look)
export const BORDER_RADIUS = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  xxl: 16,
  round: "50%",
};

// Z-index values
export const Z_INDEX = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
  overlay: 1000,
};

// Animation durations
export const ANIMATION = {
  fastest: 150,
  fast: 200,
  normal: 300,
  slow: 400,
  slowest: 500,
};

// Card elevation levels
export const ELEVATION = {
  none: 0,
  low: 1,
  medium: 2,
  high: 4,
  highest: 8,
};

// Common padding/margin presets (compact)
export const PADDING = {
  page: { xs: 1.5, sm: 2, md: 2.5 },
  card: { xs: 1, sm: 1.5, md: 2 },
  section: { xs: 1.5, sm: 2, md: 3 },
  container: { xs: 1, sm: 1.5, md: 2, lg: 3 },
};

// Common breakpoint values
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// Status colors
export const STATUS_COLORS = {
  active: "success",
  pending: "warning",
  failed: "error",
  suspended: "error",
  maintenance: "info",
  completed: "success",
  creating: "info",
  deleting: "warning",
  deleted: "default",
};

// Common sizes for components (balanced)
export const COMPONENT_SIZES = {
  avatar: {
    xs: 22,
    sm: 28,
    md: 36,
    lg: 44,
    xl: 60,
  },
  chip: {
    height: 24,
  },
  button: {
    small: { height: 30, padding: "5px 12px" },
    medium: { height: 34, padding: "6px 16px" },
    large: { height: 40, padding: "8px 22px" },
  },
};

// Common transitions
export const TRANSITIONS = {
  default: "all 0.3s ease-in-out",
  fast: "all 0.2s ease-in-out",
  slow: "all 0.4s ease-in-out",
  opacity: "opacity 0.3s ease-in-out",
  transform: "transform 0.3s ease-in-out",
};

// Table configuration (balanced)
export const TABLE = {
  rowHeight: 44,
  headerHeight: 48,
  rowsPerPageOptions: [10, 25, 50, 100],
  defaultRowsPerPage: 25,
};

// Form configuration (slightly larger)
export const FORM = {
  maxWidth: 540,
  labelMinWidth: 100,
  inputHeight: 36,
  spacing: 2,
};

// Grid configuration (compact)
export const GRID = {
  containerSpacing: { xs: 1, sm: 1.5, md: 2 },
  cardMinWidth: 240,
  cardMaxWidth: 360,
};

// Common shadow values
export const SHADOWS = {
  light: "0px 2px 4px rgba(0,0,0,0.05)",
  medium: "0px 4px 8px rgba(0,0,0,0.08)",
  heavy: "0px 8px 16px rgba(0,0,0,0.12)",
  hover: "0px 6px 12px rgba(0,0,0,0.15)",
};

// Environment colors
export const ENVIRONMENT_COLORS = {
  SANDBOX: { main: "#00c9a7", light: "#33d4b8", dark: "#008c75" },
  STAGING: { main: "#ff9800", light: "#ffac33", dark: "#b26a00" },
  PRODUCTION: { main: "#f44336", light: "#f6685e", dark: "#aa2e25" },
};

// Deployment type colors
export const DEPLOYMENT_TYPE_COLORS = {
  WORDPRESS: "#0073e6",
  DOCKER_IMAGE: "#00c9a7",
  DOCKER_COMPOSE: "#ff9800",
  GIT_DOCKERFILE: "#9c27b0",
};

const ThemeConstants = {
  SPACING,
  ICON_SIZES,
  LAYOUT,
  BORDER_RADIUS,
  Z_INDEX,
  ANIMATION,
  ELEVATION,
  PADDING,
  BREAKPOINTS,
  STATUS_COLORS,
  COMPONENT_SIZES,
  TRANSITIONS,
  TABLE,
  FORM,
  GRID,
  SHADOWS,
  ENVIRONMENT_COLORS,
  DEPLOYMENT_TYPE_COLORS,
};

export default ThemeConstants;
